import React from "react";
import {
    Alert,
    Col,
    Form,
    Row,
} from "react-bootstrap";
import { useFormikContext } from "formik";
import CountryExtraCard from "./CountryExtraCard";
import HelpTooltip from "./HelpTooltip";
import {
    COUNTRY_SERVICE_KEYS,
} from "../utils/countryExtras";

const FormSection = ({
    title,
    level = 1,
    children,
}) => {
    const Tag = `h${Math.min(6, level + 3)}`;

    return (
        <section className="mb-4 form-section">
            <Tag
                className={`text-muted ${level === 1
                        ? "fw-semibold"
                        : "fw-medium"
                    }`}
            >
                {title}
            </Tag>

            {children}
        </section>
    );
};

const hasConfiguredCountryServices = (
    services = {}
) =>
    COUNTRY_SERVICE_KEYS.some((serviceKey) => {
        const service = services?.[serviceKey];

        return (
            Boolean(service?.enabled) ||
            Boolean(service?.countries?.length)
        );
    });

const PermissionField = ({
    id,
    label,
    helpText,
    checked,
    disabled = false,
    disabledText = "",
    onChange,
}) => (
    <Form.Group className="mb-0">
        <div
            className={[
                "d-flex",
                "flex-column",
                "flex-sm-row",
                "justify-content-between",
                "align-items-sm-center",
                "gap-2",
                disabled ? "opacity-75" : "",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            <div className="d-flex align-items-center">
                <Form.Label
                    htmlFor={id}
                    className="mb-0"
                >
                    {label}
                </Form.Label>

                <HelpTooltip id={id}>
                    {helpText}
                </HelpTooltip>
            </div>

            <Form.Check
                type="switch"
                id={id}
                label={
                    checked
                        ? "Allowed"
                        : "Not allowed"
                }
                checked={checked}
                disabled={disabled}
                onChange={onChange}
                className="flex-shrink-0 mb-0"
            />
        </div>

        {disabled && disabledText && (
            <Form.Text className="text-muted d-block mt-1">
                {disabledText}
            </Form.Text>
        )}
    </Form.Group>
);

export default function TravelPermissionsSection({
    meta,
}) {
    const {
        values,
        setFieldValue,
    } = useFormikContext();

    const rentalPolicy =
        values.rentalPolicy || {};

    const canLeaveCity =
        Boolean(rentalPolicy.canLeaveCity);

    const canLeaveState =
        canLeaveCity &&
        Boolean(rentalPolicy.canLeaveState);

    const canTravelInternationally =
        canLeaveCity &&
        canLeaveState &&
        Boolean(
            rentalPolicy.countryRestriction
        );

    const countries =
        Array.isArray(meta?.countries)
            ? meta.countries
            : [];

    const hasConfiguredServices =
        hasConfiguredCountryServices(
            values.countryServices
        );

    const handleCanLeaveCityChange = (
        event
    ) => {
        const enabled =
            event.target.checked;

        setFieldValue(
            "rentalPolicy.canLeaveCity",
            enabled
        );

        if (!enabled) {
            setFieldValue(
                "rentalPolicy.canLeaveState",
                false
            );

            setFieldValue(
                "rentalPolicy.countryRestriction",
                false
            );
        }
    };

    const handleCanLeaveStateChange = (
        event
    ) => {
        const enabled =
            event.target.checked;

        setFieldValue(
            "rentalPolicy.canLeaveState",
            enabled
        );

        if (!enabled) {
            setFieldValue(
                "rentalPolicy.countryRestriction",
                false
            );
        }
    };

    const getInternationalDisabledText = () => {
        if (!canLeaveCity) {
            return "City Travel must be allowed first.";
        }

        if (!canLeaveState) {
            return "Regional Travel must be allowed first.";
        }

        return "";
    };

    return (
        <>
            <FormSection
                title="Travel Area"
                level={2}
            >
                <Row className="g-4">
                    <Col xs={12}>
                        <PermissionField
                            id="rentalPolicy.canLeaveCity"
                            label="City Travel"
                            helpText="Allow customers to travel outside the pickup city."
                            checked={canLeaveCity}
                            onChange={
                                handleCanLeaveCityChange
                            }
                        />
                    </Col>

                    <Col xs={12}>
                        <PermissionField
                            id="rentalPolicy.canLeaveState"
                            label="Regional Travel"
                            helpText="Allow customers to leave the pickup state or region while remaining in the same country."
                            checked={canLeaveState}
                            disabled={!canLeaveCity}
                            disabledText="City Travel must be allowed first."
                            onChange={
                                handleCanLeaveStateChange
                            }
                        />
                    </Col>

                    <Col xs={12}>
                        <PermissionField
                            id="rentalPolicy.countryRestriction"
                            label="International Travel"
                            helpText="Allow customers to cross national borders with this motorcycle."
                            checked={
                                canTravelInternationally
                            }
                            disabled={
                                !canLeaveCity ||
                                !canLeaveState
                            }
                            disabledText={
                                getInternationalDisabledText()
                            }
                            onChange={(event) =>
                                setFieldValue(
                                    "rentalPolicy.countryRestriction",
                                    event.target.checked
                                )
                            }
                        />
                    </Col>
                </Row>

                {!canTravelInternationally &&
                    hasConfiguredServices && (
                        <Alert
                            variant="warning"
                            className="py-2 mt-4 mb-0"
                        >
                            International travel is disabled.
                            Existing country service settings
                            are kept temporarily but will not
                            be saved.
                        </Alert>
                    )}
            </FormSection>

            {canTravelInternationally && (
                <FormSection
                    title="International Travel Services"
                    level={2}
                >
                    <div className="text-muted small mb-3">
                        Configure the services available for
                        each destination country. All fees are
                        charged once per booking.
                    </div>

                    {!countries.length && (
                        <Alert variant="warning">
                            No countries are available. Verify
                            that the backend returns{" "}
                            <code>meta.countries</code>.
                        </Alert>
                    )}

                    {COUNTRY_SERVICE_KEYS.map(
                        (serviceKey) => (
                            <CountryExtraCard
                                key={serviceKey}
                                serviceKey={
                                    serviceKey
                                }
                                countries={
                                    countries
                                }
                            />
                        )
                    )}
                </FormSection>
            )}
        </>
    );
}